/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

type ConnTokenMatcher = {
  // falsy means "no restriction" for that aspect
  types?: Set<ConnectionType>;
  idNeedles?: string[];
  op: ":" | "=";        // string op for IDs (":" contains, "=" exact)
};

const normalize = (s: unknown) => String(s ?? "").toLowerCase().trim();

export const connectionKeyMatches = (k: string) => {
  const kk = k.toLowerCase().replace(/[^a-z0-9]/g, "");
  return kk === "connection" || kk === "connectionid" || kk === "connectionservice";
};

// Parse the value of a single connection filter token into a matcher
export const parseConnToken = (valueRaw: string, op: ":" | "="): ConnTokenMatcher => {

  const types = new Set<ConnectionType>();
  const idNeedles: string[] = [];


  return {
    types: types.size ? types : undefined,
    idNeedles: idNeedles.length ? idNeedles : undefined,
    op: op === "=" ? "=" : ":",
  };
};

// Build a single item-level predicate from an array of token matchers (AND across tokens)
export const buildConnItemPredicate = (tokens: ConnTokenMatcher[]) => {
  if (!tokens.length) return undefined as
    | undefined
    | ((type: ConnectionType, item: { id: string; subscriptionId: string }) => boolean);

  return (type: ConnectionType, item: { id: string; subscriptionId: string }) => {
    const sub = normalize(item.subscriptionId);
    const id = normalize(item.id);
    const subid = normalize((item as any).connectedServiceSubId);

    // Every token must be satisfied by this (type,item)
    return tokens.every((tok) => {
      // Type guard (if token restricts types)
      if (tok.types && !tok.types.has(type)) return false;

      // ID guard (if token restricts ids)
      if (tok.idNeedles && tok.idNeedles.length) {
        if (tok.op === "=") {
          return tok.idNeedles.some((n) => n === sub || n === id || n === subid);
        }
        return tok.idNeedles.some((n) => sub.includes(n) || id.includes(n) || subid.includes(n));
      }

      return true;
    });
  };
};


export type ConnectionType =
  | "internet"
  | "cloudConnectivity"
  | "fwint"
  | "port"
  | "datacenter";

export const SVC_ALIASES: Record<ConnectionType, string[]> = {
  internet: ["internet", "ipx"],
  cloudConnectivity: ["cloud connectivity", "cloud", "cc"],
  fwint: ["fwint", "fwi"],
  port: ["port", "ports"],
  datacenter: ["datacenter", "dc", "dci"],
};

export function canonicalStatus(s: string | null | undefined): "active" | "down" | "unknown" {
  const v = String(s ?? "").toLowerCase().trim();
  if (["up", "active", "ok", "online"].includes(v)) return "active";
  if (["down", "offline", "error"].includes(v)) return "down";
  return "unknown";
}

// Human-facing label you show in the UI
export function statusLabel(s: string | null | undefined): "Active" | "Down" | "Unknown" {
  const c = canonicalStatus(s);
  if (c === "active") return "Active";
  if (c === "down") return "Down";
  return "Unknown";
}

export function normalizeStatus(s: string | null | undefined) {
  const v = String(s ?? "").toLowerCase().trim();
  if (v === "up" || v === "active") return "up";
  if (v === "down") return "down";
  if (!v) return "unknown";
  return v;
}

// Titles used in modal (and can be reused elsewhere)
export const SECTION_TITLE: Record<ConnectionType, string> = {
  internet: "Internet Connections",
  cloudConnectivity: "Cloud Connectivity",
  fwint: "FWINT Connections",
  port: "Ports",
  datacenter: "Datacenter",
};

// Tag labels (shown inside the badge)
export const TAG_LABEL: Record<ConnectionType, string> = {
  internet: "IPX",
  cloudConnectivity: "CC",
  fwint: "FWI",
  port: "PORT",
  datacenter: "DCI",
};

// Tailwind classes for colored badges
export const TAG_CLASS: Record<ConnectionType, string> = {
  internet: "bg-blue-100 text-blue-800 border-blue-200",
  cloudConnectivity: "bg-purple-100 text-purple-800 border-purple-200",
  fwint: "bg-rose-100 text-rose-800 border-rose-200",
  port: "bg-amber-100 text-amber-800 border-amber-200",
  datacenter: "bg-teal-100 text-teal-800 border-teal-200",
};

// Optional: a ready-to-use tag component
export function ServiceTag({ type }: { type: ConnectionType }) {
  return (
    <span
      className={
        "inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-semibold " +
        TAG_CLASS[type]
      }
    >
      {TAG_LABEL[type]}
    </span>
  );
}

// Service type → detail route lives once, in `serviceDetailPath`
// (@shared/navigation/tabs/detailRoutes).
